import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useSessionState } from '@/hooks/useSessionState'
import { Alert, Loader } from '@mantine/core'
import { IconCircleOff } from '@tabler/icons-react'
import { SelectPatientModal, type SelectPatientRow } from '@/components/ui'
import { ChatHeader, InputBar, MessageList, Sidebar, Welcome } from './components'
import { sumTurnUsage } from './cost'
import { useAssistant } from './useAssistant'
import { useFavorites } from './useFavorites'

export const AssistantPage = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedConversationId = searchParams.get('conversation') ?? undefined
  const resetKey = (location.state as { sectionResetKey?: number } | null)?.sectionResetKey
  const [mountedResetKey] = useState(() => resetKey)
  // Reopen the conversation the user was last in rather than always the newest
  // one (PD-1206). Frozen at mount, because `useAssistant` re-runs its boot
  // effect whenever the initial id changes and would reload on every switch.
  const [lastConversationId, setLastConversationId] = useSessionState<string | undefined>(
    'assistant:conversation',
    undefined,
  )
  const [initialConversationId] = useState(() => requestedConversationId ?? lastConversationId)
  const {
    status,
    entries,
    conversationId,
    activeThreadPath,
    conversationKey,
    conversations,
    debugTools,
    maxTokens,
    debugProviders,
    currentDebugSelection,
    selectedDebugSelection,
    currentForcedTodayDate,
    currentTimezone,
    revealConversationDetails,
    forceTodayDate,
    forcedTodayDate,
    streaming,
    liveStatus,
    startupError,
    loadError,
    actors,
    selectedActor,
    selfActor,
    showActorPicker,
    setActor,
    send,
    cancel,
    startNew,
    selectConversation,
    refreshConversations,
    deleteConversation,
    getCurrentConversation,
    setDebugProvider,
    setDebugModel,
    setRevealConversationDetails,
    setForceTodayDate,
    setForcedTodayDate,
  } = useAssistant(initialConversationId)

  const previousRequestedConversationId = useRef(requestedConversationId)
  const pendingRequestedConversationId = useRef<string | null>(null)

  useEffect(() => {
    if (status !== 'ready') return
    const previous = previousRequestedConversationId.current
    previousRequestedConversationId.current = requestedConversationId
    if (
      !requestedConversationId ||
      requestedConversationId === previous ||
      requestedConversationId === conversationId ||
      selectedActor !== selfActor
    ) {
      if (requestedConversationId === conversationId) pendingRequestedConversationId.current = null
      return
    }

    // Browser history can change only the query while this route stays mounted.
    // Load that requested conversation directly; changing the frozen boot id would
    // rerun all of useAssistant's startup work.
    pendingRequestedConversationId.current = requestedConversationId
    void selectConversation([requestedConversationId])
  }, [
    status,
    requestedConversationId,
    conversationId,
    selectedActor,
    selfActor,
    selectConversation,
  ])

  useEffect(() => {
    // Only ever remember or advertise the caller's own conversation. `setActor`
    // (the superadmin debug picker) switches the whole view to another actor's
    // threads, and a later mount has no override to send the wire `actor` param
    // with, so restoring one would reopen someone else's thread as the caller's
    // own (PD-1206). An old page can briefly observe Shell's reset navigation
    // before its keyed Outlet remount; that instance must not restore the state
    // and query the reset just cleared.
    if (
      !conversationId ||
      selectedActor !== selfActor ||
      (resetKey !== undefined && resetKey !== mountedResetKey)
    ) {
      return
    }

    const pending = pendingRequestedConversationId.current
    if (pending && conversationId !== pending) return
    pendingRequestedConversationId.current = null
    setLastConversationId(conversationId)

    if (requestedConversationId === conversationId) return
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        next.set('conversation', conversationId)
        return next
      },
      { replace: true },
    )
  }, [
    conversationId,
    requestedConversationId,
    selectedActor,
    selfActor,
    resetKey,
    mountedResetKey,
    setLastConversationId,
    setSearchParams,
  ])

  const favorites = useFavorites()
  const [inputFocusRequest, setInputFocusRequest] = useState(0)
  const [patientPickerOpen, setPatientPickerOpen] = useState(false)
  // Search hint from the turn's `artifacts.request.mask`, pre-seeded into the picker's search.
  const [patientPickerMask, setPatientPickerMask] = useState<string | undefined>(undefined)

  const submitQuestion = useCallback(
    async (text: string): Promise<'sent' | 'already_streaming'> => {
      const trimmed = text.trim()
      if (!trimmed || streaming) return 'sent'
      return send(trimmed)
    },
    [send, streaming],
  )

  // The picker's selection becomes an ordinary user message carrying a
  // `[Name](/patients/<id>)` markdown link; the assistant reads the id from the
  // link (see the chat system prompt) and resumes the pending question.
  const handlePatientSelected = useCallback(
    (patient: SelectPatientRow) => {
      const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ') || 'this patient'
      void send(`[${name}](/patients/${patient.id})`)
    },
    [send],
  )

  const startNewAndFocusInput = useCallback(async () => {
    await startNew()
    setInputFocusRequest((request) => request + 1)
  }, [startNew])

  // The Shell reserves a 64px header, plus the RECENT patients bar when it
  // has entries (it publishes its height onto --recent-bar-height). Pin the
  // chat to the remaining viewport so the message list scrolls in place
  // rather than the page.
  const heightStyle = { height: 'calc(100vh - 64px - var(--recent-bar-height, 0px))' }

  if (status === 'starting') {
    return (
      <div className="flex w-full items-center justify-center" style={heightStyle}>
        <Loader size="md" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="flex w-full items-center justify-center px-6" style={heightStyle}>
        <Alert color="red" icon={<IconCircleOff />} title="Couldn't start BeAssistant" maw={520}>
          {startupError ?? 'Unknown error.'}
        </Alert>
      </div>
    )
  }

  // Welcome screen stays up until the user sends their first message. In
  // debug mode the conversation also includes the system entry, so length
  // alone isn't a reliable "fresh chat" signal.
  const showWelcome = !entries.some((entry) => entry.kind === 'turn' && entry.user !== undefined)

  return (
    <div className="flex w-full" style={heightStyle}>
      <Sidebar
        favorites={favorites.favorites}
        favoriteTexts={favorites.favoriteSet}
        favoritesLoaded={favorites.loaded}
        isFavorite={favorites.isFavorite}
        onSelectQuestion={submitQuestion}
        onToggleFavorite={favorites.toggleFavorite}
        onUpdateFavorite={favorites.updateFavorite}
      />
      <section className="flex min-w-0 flex-1 flex-col">
        <ChatHeader
          conversations={conversations}
          conversationId={conversationId}
          activeThreadPath={activeThreadPath}
          conversationKey={conversationKey}
          debugTools={debugTools}
          debugProviders={debugProviders}
          currentDebugSelection={currentDebugSelection}
          selectedDebugSelection={selectedDebugSelection}
          revealConversationDetails={revealConversationDetails}
          conversationCostUsd={sumTurnUsage(entries)?.costUsd}
          forceTodayDate={forceTodayDate}
          forcedTodayDate={forcedTodayDate}
          actors={actors}
          selectedActor={selectedActor}
          selfActor={selfActor}
          showActorPicker={showActorPicker}
          onActorChange={(actor) => void setActor(actor)}
          onSelect={(threadPath) => void selectConversation(threadPath)}
          onOpen={() => void refreshConversations()}
          onDelete={(id) => void deleteConversation(id)}
          onNewChat={() => void startNewAndFocusInput()}
          onDebugProviderChange={setDebugProvider}
          onDebugModelChange={setDebugModel}
          onRevealConversationDetailsChange={setRevealConversationDetails}
          onForceTodayDateChange={setForceTodayDate}
          onForcedTodayDateChange={setForcedTodayDate}
          onDownloadConversation={getCurrentConversation}
        />

        {loadError ? (
          <div
            className="flex flex-1 items-center justify-center bg-[#eef2f7] px-6"
            data-testid="assistant-load-error"
          >
            <Alert
              color="red"
              icon={<IconCircleOff />}
              title="Couldn't load conversation"
              maw={520}
            >
              {loadError}
            </Alert>
          </div>
        ) : showWelcome ? (
          <div className="flex-1 overflow-y-auto bg-[#eef2f7]">
            <Welcome onSelectChip={submitQuestion} />
          </div>
        ) : (
          <MessageList
            entries={entries}
            streaming={streaming}
            liveStatus={liveStatus}
            onFollowUp={submitQuestion}
            onSelectPatient={(mask) => {
              setPatientPickerMask(mask)
              setPatientPickerOpen(true)
            }}
            isFavorite={favorites.isFavorite}
            onToggleFavorite={favorites.toggleFavorite}
            subAgentThread={(activeThreadPath?.length ?? 0) > 1}
            threadPath={activeThreadPath}
            onOpenThread={(threadPath) => void selectConversation(threadPath)}
            conversationDetails={
              revealConversationDetails
                ? {
                    provider: currentDebugSelection.provider,
                    model: currentDebugSelection.model,
                    maxTokens,
                    forcedCurrentDate: currentForcedTodayDate,
                    timezone: currentTimezone,
                    conversationKey,
                  }
                : undefined
            }
          />
        )}
        <InputBar
          streaming={streaming}
          focusRequest={inputFocusRequest}
          onSubmit={submitQuestion}
          onCancel={() => void cancel()}
        />
      </section>
      <SelectPatientModal
        opened={patientPickerOpen}
        onClose={() => {
          setPatientPickerOpen(false)
          setPatientPickerMask(undefined)
        }}
        mask={patientPickerMask}
        onSelect={handlePatientSelected}
      />
    </div>
  )
}
